import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { themeService } from 'Services/themeService';
import { secureLocalStorage } from '../utils/crypto';
import { applyThemeVariables } from '../utils/themeUtils';
import { isDevAdaptationEnabled } from '../utils/devAdaptation';
import devThemeData from '../utils/devTheme.json';

const ThemeContext = createContext();

export function ThemeProvider({ clientId, children }) {
  const [theme, setTheme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const lastAuthStateRef = useRef(null);

  const loadTheme = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // DEV ADAPTATION: usa tema local (devTheme.json) sem chamar o endpoint
      if (isDevAdaptationEnabled) {
        console.info('[DEV] Usando tema local (devTheme.json). Desative REACT_APP_DEV_ADAPTATION para usar o CMS.');
        setTheme(devThemeData);
        applyThemeVariables(devThemeData);
        return;
      }

      // Obtém o idioma atual (pode ser do i18n ou localStorage)
      const currentLanguage = localStorage.getItem('i18nextLng') || 'pt-BR';
      const normalizedLang = currentLanguage === 'pt' ? 'pt-BR' :
                            currentLanguage === 'en' ? 'en-US' :
                            currentLanguage;

      // Busca tema usando o novo serviço CMS
      // O themeService já gerencia o profile baseado na autenticação:
      // - Se autenticado: usa profile do usuário (do JWT)
      // - Se não autenticado: usa SEGMENT_DEFAULT dos feature flags
      const themeData = await themeService.fetchTheme(clientId, normalizedLang);
      
      if (themeData) {
        setTheme(themeData);
        applyThemeVariables(themeData);
        localStorage.setItem('theme', JSON.stringify(themeData));
      } else {
        // Fallback para cache se disponível
        const cachedTheme = localStorage.getItem('theme');
        if (cachedTheme) {
          const cachedThemeData = JSON.parse(cachedTheme);
          setTheme(cachedThemeData);
          applyThemeVariables(cachedThemeData);
        }
      }
    } catch (err) {
      console.error('Error loading theme:', err);
      setError(err);
      const cachedTheme = localStorage.getItem('theme');
      if (cachedTheme) {
        const themeData = JSON.parse(cachedTheme);
        setTheme(themeData);
        applyThemeVariables(themeData);
      }
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  // Carrega tema quando clientId muda
  useEffect(() => {
    if (clientId) {
      loadTheme();
    }
  }, [clientId, loadTheme]);

  // Monitora mudanças de autenticação para recarregar tema
  useEffect(() => {
    if (!clientId) return;

    const checkAuthChange = () => {
      const currentToken = secureLocalStorage.getItem('idToken');
      const currentAuthState = currentToken ? 'authenticated' : 'unauthenticated';
      
      // Se o estado de autenticação mudou, recarrega o tema
      if (lastAuthStateRef.current !== null && lastAuthStateRef.current !== currentAuthState) {
        // Limpa cache do themeService e localStorage para forçar recarregamento com profile correto
        themeService.clearCacheForClient(clientId);
        localStorage.removeItem('theme');
        loadTheme();
      }
      
      lastAuthStateRef.current = currentAuthState;
    };

    // Verifica mudanças periodicamente e em eventos de storage
    const interval = setInterval(checkAuthChange, 1000);
    
    const handleStorageChange = (e) => {
      if (e.key === 'idToken' || e.key === 'tokenExpiresAt') {
        checkAuthChange();
      }
    };
    
    // Escuta mudanças no localStorage (para múltiplas abas)
    window.addEventListener('storage', handleStorageChange);
    
    // Escuta evento customizado de atualização de tema
    window.addEventListener('themeUpdated', loadTheme);
    
    // Escuta evento de login para recarregar tema após login
    const handleUserLoggedIn = async () => {
      // Aguarda um pouco para garantir que o segment foi carregado
      setTimeout(() => {
        themeService.clearCacheForClient(clientId);
        localStorage.removeItem('theme');
        loadTheme();
      }, 200);
    };
    
    window.addEventListener('userLoggedIn', handleUserLoggedIn);
    
    // Escuta evento de segment carregado para recarregar tema imediatamente
    const handleUserSegmentLoaded = () => {
      themeService.clearCacheForClient(clientId);
      localStorage.removeItem('theme');
      loadTheme();
    };
    
    window.addEventListener('userSegmentLoaded', handleUserSegmentLoaded);
    
    // Verifica estado inicial
    checkAuthChange();
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('themeUpdated', loadTheme);
      window.removeEventListener('userLoggedIn', handleUserLoggedIn);
      window.removeEventListener('userSegmentLoaded', handleUserSegmentLoaded);
    };
  }, [clientId, loadTheme]);

  // Note: applyThemeVariables is now imported from utils/themeUtils

  return (
    <ThemeContext.Provider value={{ theme, loading, error }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
} 
